import { type QueryKey, useInfiniteQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { get } from "common/helpers/HTTP";

import type { InfiniteData } from "@tanstack/query-core";
import type { ConversationMessage } from "../types";

const itemsPerPage = 80;
type PagingParams = { before: number | "now" } | { after: number | "now" };

async function getMessages(
  workspaceId: string,
  accountId: string,
  pagingParams: PagingParams,
  conversationUrn: string,
): Promise<ConversationMessage[]> {
  const searchParams = new URLSearchParams();
  searchParams.append("count", itemsPerPage.toString());
  if ("before" in pagingParams) {
    searchParams.append(
      "created_before",
      (pagingParams.before === "now"
        ? Date.now()
        : pagingParams.before
      ).toString(),
    );
  } else {
    searchParams.append(
      "created_after",
      (pagingParams.after === "now"
        ? Date.now()
        : pagingParams.after
      ).toString(),
    );
  }

  const { messages } = await get(
    `workspaces/${workspaceId}/accounts/${accountId}/conversations/${conversationUrn}?${searchParams.toString()}`,
  );
  if ("after" in pagingParams) {
    return messages.toReversed();
  }
  return messages;
}
export default function useConversationMessages(
  params:
    | {
        accountId: string;
        conversationUrn: string;
        unreadCount: number;
        lastActivityAt: number | undefined;
      }
    | undefined,
) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { conversationsKeys } = getQueryKeys(workspaceId);

  const {
    isFetchingNextPage: isLoadingNext,
    isFetchingPreviousPage: isLoadingPrevious,
    isLoading,
    data: messages,
    fetchNextPage: loadNextMessages,
    fetchPreviousPage: loadPreviousMessages,
    hasNextPage: hasNextMessages,
    hasPreviousPage: hasPreviousMessages,
    refetch: refetchMessages,
  } = useInfiniteQuery<
    ConversationMessage[],
    unknown,
    InfiniteData<ConversationMessage[], PagingParams>,
    QueryKey,
    PagingParams
  >({
    queryKey: params
      ? conversationsKeys.messages(
          params.conversationUrn,
          params.accountId,
          params.lastActivityAt,
        )
      : undefined,
    queryFn: params
      ? ({ pageParam }) =>
          getMessages(
            workspaceId,
            params.accountId,
            pageParam,
            params.conversationUrn,
          )
      : undefined,
    enabled: !!params,
    initialPageParam: params && {
      before: params.lastActivityAt ? params.lastActivityAt + 1 : "now",
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.length >= itemsPerPage) {
        return { before: lastPage[lastPage.length - 1].created_at };
      }
      // Empty page. If it came from an `{ after }` cursor (asking for newer
      // messages) there simply are none — re-anchor to the newest messages
      // instead of terminating. This is what recovers a refetch whose first
      // stored page param is an `{ after }` cursor (left behind by a
      // fetchPreviousPage): its page 0 comes back empty, and without this the
      // refetch would stop on that empty page and collapse the whole thread.
      // An empty `{ before }` page means we've reached the start of the
      // conversation → stop paging older.
      if (lastPageParam && "after" in lastPageParam) {
        return { before: "now" };
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage, _, firstPageParam) => {
      if (firstPage.length >= itemsPerPage) {
        if ("before" in firstPageParam && firstPageParam.before === "now") {
          return { after: "now" };
        }
        return { after: firstPage[0].created_at };
      }
      return undefined;
    },
    maxPages: 6,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (params?.unreadCount && !params.lastActivityAt) {
      refetchMessages();
    }
  }, [params?.unreadCount, params?.conversationUrn]);

  async function loadMessages(param: "next" | "prev") {
    if (param === "next") {
      await loadNextMessages();
    } else {
      await loadPreviousMessages();
    }
  }

  return {
    messages,
    refetchMessages,
    loadMessages,
    isLoadingMessages: {
      next: isLoadingNext,
      prev: isLoadingPrevious,
      all: isLoading,
    },
    hasMoreMessages: { next: hasNextMessages, prev: hasPreviousMessages },
  };
}
